import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export function getYearFilter(year) {
  if (!year) return [];
  const startOfYear = new Date(year, 0, 1);
  const endOfYear = new Date(year, 11, 31, 23, 59, 59, 999);
  return [where("date", ">=", startOfYear), where("date", "<=", endOfYear)];
}

// -------------------- READ CACHE / DEDUPE --------------------
// Rarely-changing reads (visibility, posts, events, gallery, configs, donation
// totals) are commonly fetched by more than one component within the same
// page load (e.g. NavBar + Home both need visibility). Caching the in-flight
// promise means simultaneous callers share a single Firestore read instead of
// issuing one each, and the short TTL keeps quick remounts (nav back to Home)
// from re-hitting Firestore for data that hasn't changed. Writes below
// explicitly invalidate the relevant keys so admin edits show up immediately.
const READ_CACHE_TTL_MS = 30 * 1000;
const readCache = new Map(); // key -> { expiry, promise }

function cachedRead(key, fetcher, ttl = READ_CACHE_TTL_MS) {
  const cached = readCache.get(key);
  if (cached && cached.expiry > Date.now()) {
    return cached.promise;
  }
  const promise = fetcher().catch((error) => {
    readCache.delete(key); // never cache a rejected read
    throw error;
  });
  readCache.set(key, { expiry: Date.now() + ttl, promise });
  return promise;
}

function invalidateReadCache(prefix) {
  for (const key of readCache.keys()) {
    if (key.startsWith(prefix)) readCache.delete(key);
  }
}

// Shared, cached fetch of a collection's year-filtered docs so that
// getSumFromCollection and getTopNFromCollection (frequently called back to
// back for the same collection+year, e.g. on the Home page) reuse one
// Firestore read instead of two.
function getYearFilteredDocs(collectionName, year) {
  const key = `docs:${collectionName}:${year || "all"}`;
  return cachedRead(key, async () => {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...getYearFilter(year));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  });
}

// -------------------- DONATION --------------------
export async function createDonationEntry(
  collectionName,
  { name, address, amount, date, isPending, description, spendBy, images },
) {
  const currentCollection = collectionName?.collection || collectionName;
  try {
    const payload =
      currentCollection === "donation"
        ? {
            name,
            nameLower: name.toLowerCase(),
            address,
            amount,
            date,
            isPending,
          }
        : { description, spendBy, amount, date, images, isPending };

    const result = await addDoc(collection(db, currentCollection), payload);
    invalidateReadCache(`docs:${currentCollection}:`);
    return { message: "Created Entry Successfully", data: result };
  } catch (e) {
    console.error("Error adding document: ", e);
    throw new Error("Failed to create entry");
  }
}

export async function getDonationEntries(
  collectionName,
  searchTerm = "",
  getAll = false,
  filterDate = null,
  year,
) {
  const currentCollection = collectionName?.collection || collectionName;
  try {
    let baseQuery = collection(db, currentCollection);

    // Search filter
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      baseQuery = query(
        baseQuery,
        where("nameLower", ">=", lowerCaseSearchTerm),
        where("nameLower", "<=", lowerCaseSearchTerm + "\uf8ff"),
        orderBy("nameLower"),
      );
    }

    // Year filter
    const yearFilters = getYearFilter(year);
    if (yearFilters.length > 0) {
      baseQuery = query(baseQuery, ...yearFilters);
    }

    // Specific date filter
    if (filterDate) {
      const startOfDay = new Date(filterDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(filterDate);
      endOfDay.setHours(23, 59, 59, 999);
      baseQuery = query(
        baseQuery,
        where("date", ">=", startOfDay),
        where("date", "<=", endOfDay),
      );
    }

    baseQuery = query(baseQuery, orderBy("date", "desc"));

    const querySnapshot = await getDocs(baseQuery);
    const donationList = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    let totalCount = donationList.length;
    if (!getAll) {
      const totalQuery = query(
        collection(db, currentCollection),
        where("nameLower", ">=", searchTerm.toLowerCase() || ""),
        where("nameLower", "<=", (searchTerm.toLowerCase() || "") + "\uf8ff"),
      );
      const totalCountSnapshot = await getCountFromServer(totalQuery);
      totalCount = totalCountSnapshot.data().count;
    }

    return {
      message: "Entries fetched successfully",
      data: donationList,
      totalCount,
    };
  } catch (error) {
    console.error("Error fetching documents:", error);
    return { message: "An error occurred", data: [], totalCount: 0 };
  }
}

export async function updateDonation(
  collectionName,
  { name, address, amount, date, isPending, description, spendBy, images = [] },
  id,
) {
  const currentCollection = collectionName?.collection || collectionName;
  const payload =
    currentCollection === "donation"
      ? { name, address, amount, date, isPending }
      : { description, spendBy, amount, date, images, isPending };

  try {
    const todoRef = doc(db, currentCollection, id);
    await updateDoc(todoRef, payload);
    invalidateReadCache(`docs:${currentCollection}:`);
    return {
      message: "Donation Updated Successfully",
      data: { id, name, address, amount, date, isPending },
    };
  } catch (error) {
    console.error("Error updating document: ", error);
    throw new Error("Failed to update entry");
  }
}

export async function deleteDonation(collectionName, id) {
  const currentCollection = collectionName?.collection || collectionName;
  try {
    const todoRef = doc(db, currentCollection, id);
    await deleteDoc(todoRef);
    invalidateReadCache(`docs:${currentCollection}:`);
    return { message: "Deleted Entry Successfully", data: null };
  } catch (error) {
    console.error("Error deleting document: ", error);
    return { message: "Error deleting entry", data: null };
  }
}

// -------------------- UPDATE ACTIVITY (AUDIT LOG) --------------------
export async function createAuditLog({
  collectionName,
  entryId,
  entryLabel,
  changes,
  changedByName,
  changedByEmail,
  action = "updated",
}) {
  try {
    const payload = {
      collectionName,
      entryId,
      entryLabel: entryLabel || "",
      changes,
      changedByName: changedByName || "",
      changedByEmail: changedByEmail || "",
      action,
      date: new Date(),
    };
    await addDoc(collection(db, "auditLogs"), payload);
  } catch (e) {
    console.error("Error logging audit entry: ", e);
  }
}

export async function getAuditLogs() {
  try {
    const q = query(
      collection(db, "auditLogs"),
      orderBy("date", "desc"),
      limit(200),
    );
    const querySnapshot = await getDocs(q);
    const logs = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return { message: "Audit Logs Fetched Successfully", data: logs };
  } catch (e) {
    console.error("Error fetching audit logs: ", e);
    return { message: "Error fetching audit logs", data: [] };
  }
}

// -------------------- SUM & TOP --------------------
export const getSumFromCollection = async (collectionName, key, year) => {
  if (typeof collectionName !== "string" || typeof key !== "string") {
    throw new Error("Invalid input types.");
  }

  try {
    const docs = await getYearFilteredDocs(collectionName, year);
    return docs.reduce((acc, data) => {
      const value = Number(data[key]);
      if (!Number.isNaN(value) && data.isPending !== true) {
        return acc + value;
      }
      return acc;
    }, 0);
  } catch (error) {
    console.error(`Error fetching collection '${collectionName}':`, error);
    throw error;
  }
};

export const getTopNFromCollection = async (
  collectionName,
  key,
  topN,
  year,
) => {
  try {
    // Filtered/sorted in memory (not via Firestore where/orderBy) for two
    // reasons: (1) combining an equality filter (isPending) with a range
    // filter (date) on a different field needs a composite index that
    // isn't provisioned here, and (2) some existing documents have
    // `amount` stored as a string instead of a number, and Firestore's
    // orderBy sorts by type first, putting all strings above all numbers
    // regardless of value.
    const docs = (await getYearFilteredDocs(collectionName, year)).filter(
      (d) => d.isPending !== true,
    );
    docs.sort((a, b) => Number(b[key]) - Number(a[key]));
    return docs.slice(0, topN);
  } catch (error) {
    console.error(`Error fetching collection '${collectionName}':`, error);
    return [];
  }
};

// -------------------- MEMBERS --------------------
export async function createMember({ designation, image, important, name }) {
  const currentCollection = "members";
  try {
    let index = 0;

    if (important) {
      const importantQuery = query(
        collection(db, currentCollection),
        where("important", "==", true),
      );
      const querySnapshot = await getDocs(importantQuery);
      index = querySnapshot.size + 1;
    }

    const payload = {
      designation,
      image,
      important,
      name,
      ...(important && { index }),
      date: new Date(), // Ensure date is added so year filter works
    };

    const result = await addDoc(collection(db, currentCollection), payload);
    invalidateReadCache("members");
    if (result) return { message: "Created Entry Successfully", data: result };
  } catch (e) {
    console.error("Error adding document: ", e);
    throw new Error("Failed to create member entry");
  }
}

export async function getMembers() {
  const currentCollection = "members";
  try {
    const members = await cachedRead(currentCollection, async () => {
      const q = query(collection(db, currentCollection));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    });
    return { message: "Fetched Members Successfully", data: members };
  } catch (e) {
    console.error("Error getting documents: ", e);
    return { message: "Error Fetching Members", error: e };
  }
}

// -------------------- GALLERY --------------------
export async function createGalleryEntry(images) {
  const currentCollection = "gallery";
  const documentId = "singleGalleryEntry";
  const galleryDocRef = doc(db, currentCollection, documentId);

  let q = query(galleryDocRef);

  try {
    const docSnapshot = await getDoc(q);

    if (docSnapshot.exists()) {
      await updateDoc(galleryDocRef, { images: arrayUnion(...images) });
      invalidateReadCache("gallery");
      return { message: "Updated Gallery Entry Successfully" };
    } else {
      await setDoc(galleryDocRef, { images, date: new Date() });
      invalidateReadCache("gallery");
      return { message: "Created Gallery Entry Successfully" };
    }
  } catch (e) {
    console.error("Error adding or updating document: ", e);
    throw new Error("Failed to create or update gallery entry");
  }
}
export async function getGalleryEntry() {
  try {
    const currentCollection = "gallery";
    const documentId = "singleGalleryEntry";

    const data = await cachedRead(currentCollection, async () => {
      const galleryDocRef = doc(db, currentCollection, documentId);
      const docSnapshot = await getDoc(galleryDocRef);
      return docSnapshot.exists() ? docSnapshot.data() : null;
    });

    return data
      ? { data, message: "Gallery Entry Retrieved Successfully" }
      : { data: null, message: "No Gallery Entry Found" };
  } catch (error) {
    console.error("Error retrieving document:", error);
    return { data: null, message: "Failed to retrieve gallery entry" };
  }
}

// -------------------- MEMBERS (manage) --------------------
export async function updateMember(
  id,
  { designation, image, important, name },
) {
  const currentCollection = "members";
  try {
    const memberRef = doc(db, currentCollection, id);
    await updateDoc(memberRef, { designation, image, important, name });
    invalidateReadCache(currentCollection);
    return { message: "Member Updated Successfully" };
  } catch (e) {
    console.error("Error updating member: ", e);
    throw new Error("Failed to update member");
  }
}

export async function deleteMember(id) {
  const currentCollection = "members";
  try {
    await deleteDoc(doc(db, currentCollection, id));
    invalidateReadCache(currentCollection);
    return { message: "Member Deleted Successfully" };
  } catch (e) {
    console.error("Error deleting member: ", e);
    throw new Error("Failed to delete member");
  }
}

// -------------------- EVENTS --------------------
export async function createEvent({
  title,
  description,
  date,
  time,
  location,
  image,
}) {
  const currentCollection = "events";
  try {
    const payload = {
      title,
      description: description || "",
      date,
      time: time || "",
      location: location || "",
      image: image || "",
      createdAt: new Date(),
    };
    const result = await addDoc(collection(db, currentCollection), payload);
    invalidateReadCache(currentCollection);
    return { message: "Event Created Successfully", data: result };
  } catch (e) {
    console.error("Error creating event: ", e);
    throw new Error("Failed to create event");
  }
}

export async function getEvents() {
  const currentCollection = "events";
  try {
    const events = await cachedRead(currentCollection, async () => {
      const q = query(
        collection(db, currentCollection),
        orderBy("date", "asc"),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    });
    return { message: "Events Fetched Successfully", data: events };
  } catch (e) {
    console.error("Error fetching events: ", e);
    return { message: "Error fetching events", data: [] };
  }
}

export async function updateEvent(
  id,
  { title, description, date, time, location, image },
) {
  const currentCollection = "events";
  try {
    const eventRef = doc(db, currentCollection, id);
    await updateDoc(eventRef, {
      title,
      description: description || "",
      date,
      time: time || "",
      location: location || "",
      image: image || "",
    });
    invalidateReadCache(currentCollection);
    return { message: "Event Updated Successfully" };
  } catch (e) {
    console.error("Error updating event: ", e);
    throw new Error("Failed to update event");
  }
}

export async function deleteEvent(id) {
  const currentCollection = "events";
  try {
    await deleteDoc(doc(db, currentCollection, id));
    invalidateReadCache(currentCollection);
    return { message: "Event Deleted Successfully" };
  } catch (e) {
    console.error("Error deleting event: ", e);
    throw new Error("Failed to delete event");
  }
}

// -------------------- POSTS / ANNOUNCEMENTS --------------------
export async function createPost({ title, content, image, pinned }) {
  const currentCollection = "posts";
  try {
    const payload = {
      title,
      content: content || "",
      image: image || "",
      pinned: !!pinned,
      createdAt: new Date(),
    };
    const result = await addDoc(collection(db, currentCollection), payload);
    invalidateReadCache(currentCollection);
    return { message: "Post Created Successfully", data: result };
  } catch (e) {
    console.error("Error creating post: ", e);
    throw new Error("Failed to create post");
  }
}

export async function getPosts() {
  const currentCollection = "posts";
  try {
    const posts = await cachedRead(currentCollection, async () => {
      const q = query(
        collection(db, currentCollection),
        orderBy("createdAt", "desc"),
      );
      const querySnapshot = await getDocs(q);
      const fetched = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      fetched.sort((a, b) => (b.pinned === true) - (a.pinned === true));
      return fetched;
    });
    return { message: "Posts Fetched Successfully", data: posts };
  } catch (e) {
    console.error("Error fetching posts: ", e);
    return { message: "Error fetching posts", data: [] };
  }
}

export async function updatePost(id, { title, content, image, pinned }) {
  const currentCollection = "posts";
  try {
    const postRef = doc(db, currentCollection, id);
    await updateDoc(postRef, {
      title,
      content: content || "",
      image: image || "",
      pinned: !!pinned,
    });
    invalidateReadCache(currentCollection);
    return { message: "Post Updated Successfully" };
  } catch (e) {
    console.error("Error updating post: ", e);
    throw new Error("Failed to update post");
  }
}

export async function deletePost(id) {
  const currentCollection = "posts";
  try {
    await deleteDoc(doc(db, currentCollection, id));
    invalidateReadCache(currentCollection);
    return { message: "Post Deleted Successfully" };
  } catch (e) {
    console.error("Error deleting post: ", e);
    throw new Error("Failed to delete post");
  }
}

// -------------------- GALLERY (manage) --------------------
export async function deleteGalleryImage(url) {
  const currentCollection = "gallery";
  const documentId = "singleGalleryEntry";
  const galleryDocRef = doc(db, currentCollection, documentId);
  try {
    const docSnapshot = await getDoc(galleryDocRef);
    if (docSnapshot.exists()) {
      const images = (docSnapshot.data()?.images || []).filter(
        (img) => img !== url,
      );
      await updateDoc(galleryDocRef, { images });
      invalidateReadCache(currentCollection);
      return { message: "Image Removed Successfully" };
    }
    return { message: "No Gallery Entry Found" };
  } catch (e) {
    console.error("Error removing gallery image: ", e);
    throw new Error("Failed to remove gallery image");
  }
}

// -------------------- CONFIG --------------------
export async function toggleConfigEntry(configName, state) {
  const currentCollection = "config";
  const documentId = configName;
  const configRef = doc(db, currentCollection, documentId);

  try {
    if (
      typeof state !== "string" &&
      typeof state !== "number" &&
      typeof state !== "boolean" &&
      typeof state !== "object"
    ) {
      throw new Error("Unsupported data type for state");
    }

    const docSnapshot = await getDoc(configRef);
    if (docSnapshot.exists()) {
      await updateDoc(configRef, { [configName]: state, date: new Date() });
      invalidateReadCache(currentCollection);
      invalidateReadCache("visibility");
      return { message: "Config updated Successfully" };
    } else {
      await setDoc(configRef, { [configName]: state, date: new Date() });
      invalidateReadCache(currentCollection);
      invalidateReadCache("visibility");
      return { message: "Config added Successfully" };
    }
  } catch (e) {
    console.error("Error adding or updating document: ", e);
    throw new Error("Failed to create or update config entry");
  }
}

export async function getAllConfigs() {
  const currentCollection = "config";
  try {
    const configs = await cachedRead(`${currentCollection}:all`, async () => {
      const q = query(collection(db, currentCollection));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    });
    return configs.length > 0
      ? { data: configs, message: "Configs Retrieved Successfully" }
      : { data: [], message: "No Configs Found" };
  } catch (e) {
    console.error("Error retrieving documents: ", e);
    throw new Error("Failed to retrieve config entries");
  }
}

export const DEFAULT_VISIBILITY = {
  showGallery: true,
  showEvents: true,
  showPosts: true,
  showCommunity: true,
  showDonations: true,
  showExpenses: true,
  showLiveStream: true,
};

export async function getVisibilitySettings() {
  try {
    return await cachedRead("visibility", async () => {
      const configRef = doc(db, "config", "visibility");
      const docSnapshot = await getDoc(configRef);
      if (docSnapshot.exists()) {
        return {
          ...DEFAULT_VISIBILITY,
          ...(docSnapshot.data()?.visibility || {}),
        };
      }
      return { ...DEFAULT_VISIBILITY };
    });
  } catch (e) {
    console.error("Error fetching visibility settings: ", e);
    return { ...DEFAULT_VISIBILITY };
  }
}

export async function updateVisibilitySettings(visibility) {
  return toggleConfigEntry("visibility", visibility);
}
